import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ImportType, importTypes, importFields } from 'globals/store/import.data';
import { DropdownOption } from 'globals/store/programs.data';
import { Wrapper } from '../wrapper.component';
import { JoiEvent } from 'globals/store/events.data';
import { DialogWrapperComponent } from '../wrappers/dialog-wrapper.component';
import { CommonModule } from '@angular/common';
import { DropdownComponent } from '../misc/dropdown.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BigDialogWrapperComponent } from '../wrappers/big-dialog-wrapper.component';

@Component({
    selector: 'import-dialog',
    template: `
<wide-dialog-wrapper
  [class]="'padded'"
  [successLabel]="'IMPORT'"
  [delete]="false"
  [dialogTitle]="'Import '+data.type"
  [backgroundColor]="'#41474f'"
  [whiteTitle]="true"
  [midLeftLabel]="'Close'"
  [disabled]="!hasDataToImport"
  (onMidLeft)="close()"
  (onSubmit)="submit()"
>
  <div class="import_dialog_wrapper">
    <div class="import_dialog_content">
      <div *ngIf="!data.sheet">
        <h3>Spreadsheet import error</h3>
        <div>
          Something went wrong while importing your data. Please try a different .xlsx, .xls or .csv file.
        </div>
      </div>
      <div *ngIf="data.sheet">
        <h3>Match spreadsheet column to information</h3>
        <div>
          <mat-checkbox class="pad-right" color="primary" [checked]="hasHeadingsRow" (change)="toggleHeadRow()">First row contains headings</mat-checkbox>
          <mat-checkbox *ngIf="data.type === importTypes.performer || data.type === importTypes.attendee" color="primary" [checked]="hasLastName" (change)="toggleLastNameColumn()">First name and last name are in separate columns</mat-checkbox>
        </div>
        <div class="dropdowns" *ngIf="fields">
          <div class="dropdown" [hidden]="hasLastName && field.replaceable || !hasLastName && field.mergeReplacementForKey" *ngFor="let field of fields; index as index">
            <div class="note placeholders">{{ (!hasLastName || !field.multicolumn ? field.label : field.multicolumn.label) + (field.isRequired ? ' *' : '')}}</div>
            <dropdown *ngIf="!hasLastName || !field.multicolumn" [selections]="headings" [selected]="selectedOptions[index] ? headings[selectedOptions[index]] : headings[headings.length - 1]" (select)="pairHeading($event.id, index)"></dropdown>
            <div class="multi" *ngIf="hasLastName && field.multicolumn">
              <dropdown *ngFor="let mfield of field.multicolumn.fields; index as mf_index" [selections]="headings" [selected]="selectedOptions[(index + '.' + mf_index)] ? headings[selectedOptions[(index + '.' + mf_index)]] : headings[headings.length - 1]" (select)="pairHeading($event.id, index, mf_index)"></dropdown>
            </div>
          </div>
        </div>
        <h3>Spreadsheet preview</h3>
        <div>Here are the first {{previewRowsNumber}} rows of your spreadsheet</div>
        <div class="spreadsheet">
          <div class="column" [ngClass]="{unused:!selectedColumns[column_index]}" *ngFor="let column of topFive; index as column_index" class="column" >
            <div class="cell" 
              *ngFor="let cell of column; index as row_index" 
              [ngClass]="{header:row_index===0, empty:cell === undefined}"
            >{{cell}}</div>
          </div>
          <div class="column last" *ngIf="topFive[topFive.length-1] as blankColumn">
            <div class="cell" *ngFor="let cell of blankColumn; index as row_index"  [ngClass]="{header:row_index===0}"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</wide-dialog-wrapper>
  `,
    styleUrls: ['./import.dialog.scss'],
    standalone: true,
    imports: [CommonModule, DropdownComponent, MatCheckboxModule, BigDialogWrapperComponent]
})
export class ImportDialogComponent extends Wrapper<ImportDialogComponent> implements OnInit {

  hasLastName = false;
  hasHeadingsRow = false;
  importTypes = importTypes;
  event: JoiEvent;
  fields
  previewRowsNumber = 5
  alphabet = "abcdefghijklmnopqrstuvwxyz".toUpperCase().split("");
  replacementKeys = ['name']
  importedHeadings:DropdownOption[] = []
  alphabetHeadings:DropdownOption[] = []
  headings:DropdownOption[] = []
  selectedOptions:{[index: string]: string} = {}
  selectedColumns:{[index:string]: boolean} = {}
  topFive;
  hasDataToImport = false;
  customQuestionPrefix = '_customQuestion-';

  constructor(
    public dialogRef: MatDialogRef<ImportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImportDialogData,
  ) { 
    super();
  }

  async ngOnInit(){
    this.hasLastName = this.data.type === importTypes.performer
    this.fields = importFields[this.data.type]
    this.subProp(this.store.select('events','event'), 'event')
    // TODO: switch depending on import type
    switch(this.data.type){
      case importTypes.exhibitor:
        const customQuestions = this.event?.exhibitorQuestions?.map((q, index)=>({
          key: this.customQuestionPrefix + q.id,
          label: q.question.replace(/<[^>]*>/g, ''),
          isRequired: false,
          replaceable: true
        })) || []
        this.fields = this.fields.concat(customQuestions);
        break;
      case importTypes.attendee:
        const attendeeQuestions = this.event?.attendeeQuestions?.map((q, index)=>({
          key: this.customQuestionPrefix + q.id,
          label: q.question.replace(/<[^>]*>/g, ''),
          isRequired: false,
          replaceable: true
        })) || []
        this.fields = this.fields.concat(attendeeQuestions);
        break;
      case importTypes.sponsor:
        const sponsorQuestions = this.event?.sponsorQuestions?.map((q, index)=>({
          key: this.customQuestionPrefix + q.id,
          label: q.question.replace(/<[^>]*>/g, ''),
          isRequired: false,
          replaceable: true
        })) || []
        this.fields = this.fields.concat(sponsorQuestions);
        break;
      case importTypes.performer:
        const performerQuestions = this.event?.performerQuestions?.map((q, index)=>({
          key: this.customQuestionPrefix + q.id,
          label: q.question.replace(/<[^>]*>/g, ''),
          isRequired: false,
          replaceable: true
        })) || []
        this.fields = this.fields.concat(performerQuestions);
        break;
      case importTypes.resource:
        const resourceQuestions = this.event?.resourceQuestions?.map((q, index)=>({
          key: this.customQuestionPrefix + q.id,
          label: q.question.replace(/<[^>]*>/g, ''),
          isRequired: false,
          replaceable: true
        })) || []
        this.fields = this.fields.concat(resourceQuestions);
        break;
    }
    if(this.fields && this.data.sheet && this.data.sheet[0] && this.data.sheet[0][0]) { 
      this.setHeadings(true)
    }else{
      this.data.sheet = null 
    }
  }

  setHeadings(init?:boolean){
    const allFields = [...this.fields ].concat(...this.fields.filter(field=>field.multicolumn).map((field,i)=>field.multicolumn.fields.map((mfield,mindex)=>({...mfield, mindex:i+'.'+mindex, isMulti:true}))),[]);
    const mini_keys = allFields.map((k,i)=>({index:i, mindex:k.mindex, isMulti:k.isMulti, hasMulti:!!k.multicolumn, key:k.key.replace(' ','').toLowerCase(),pool:k.sudo ? k.sudo : []})).filter(field=>(this.hasLastName && !field.hasMulti) || (!this.hasLastName && !field.isMulti));
    this.importedHeadings = [];
    this.alphabetHeadings = [];
    let atPresentButNotInFirstRow:boolean;
    this.data.sheet[0].forEach( (heading, optionIndex) => {
      this.importedHeadings.push({id:optionIndex+"", title:heading})
      this.alphabetHeadings.push({id:optionIndex+"", title:this.makeColumnOption(this.alphabet[optionIndex])})
      atPresentButNotInFirstRow = (!/@/.test(heading))
      const minified = heading.replace(' ', '').toLowerCase();
      const k_idx = mini_keys.find((k)=>k.key===minified || k.pool.includes(minified));
      if( k_idx && this.selectedOptions[k_idx.index] === undefined) {
        if(init) this.hasHeadingsRow = true;
        this.selectedOptions[k_idx.mindex !== undefined ? k_idx.mindex : k_idx.index] = optionIndex+"";
      }
    });
    if(init && atPresentButNotInFirstRow && !this.hasHeadingsRow){
      atPresentButNotInFirstRow = false;
      this.data.sheet.some(row=>{
        row.some(col=>{
          if(/@/.test(col)){
            atPresentButNotInFirstRow = true;
            return true;
          }
        })
        return atPresentButNotInFirstRow;
      })
      if(atPresentButNotInFirstRow) this.hasHeadingsRow = true;
    }
    this.setTopFive();
    this.headings = this.hasHeadingsRow ? this.importedHeadings : this.alphabetHeadings;
    this.headings.push({id:this.headings.length+"", title:'(Not included)'})
    this.checkRequiredColumnsAreMatched();
  }

  setTopFive(){
    const truncated:string[][] = this.data.sheet.slice(0,this.previewRowsNumber + 1)
    if(!this.hasHeadingsRow){
      truncated.pop();
      truncated.unshift(this.alphabetHeadings.map((heading)=>heading.title))
    }
    this.topFive = this.importedHeadings.map((option, idx)=>truncated.map(row=>row[idx]))
  }

  setUsedColumns(){
    this.selectedColumns = Object.values(this.selectedOptions).reduce((rtn, value) => {
      rtn[value] = true;
      return rtn;
    }, {});
  }

  checkRequiredColumnsAreMatched(){
    if(!this.selectedOptions) return;
    this.hasDataToImport = false;
    if(Object.keys(this.selectedOptions).length === 0) return;    
    let allFields = [...this.fields ]
    if(this.hasLastName)  allFields = allFields.concat(...this.fields.filter(field=>field.multicolumn).map((field,i)=>field.multicolumn.fields.map((mfield,mindex)=>({...mfield, mindex:i+'.'+mindex}))),[]).filter(field=>!field.multicolumn);
    this.hasDataToImport = allFields.map((field, index)=>({...field, index})).filter(field=>field.isRequired).every(field=>{
      return !!this.selectedOptions[field.mindex ? field.mindex : field.index]});
    this.setUsedColumns();
  }

  makeColumnOption = (letter:string)=>"Column "+letter;

  pairHeading(optionIndex:string, index:number, subindex?:number){
    const fieldIndex = subindex === undefined ? index : index + "." + subindex;
    this.selectedOptions[fieldIndex] = optionIndex;
    this.checkRequiredColumnsAreMatched();
  }

  toggleHeadRow(){
    this.hasHeadingsRow = !this.hasHeadingsRow;
    this.setHeadings();
  }

  toggleLastNameColumn(){
    this.hasLastName = !this.hasLastName;
    this.checkRequiredColumnsAreMatched();
  }

  close() {
    this.dialogRef.close(null)
  }

  submit() {
    const usedFields = this.fields.map((field, index)=>({...field, index})).filter((field, fieldIndex)=> this.selectedOptions.hasOwnProperty(fieldIndex) || field.multicolumn )
    const entries = this.data.sheet.filter((row:string[],rowIndex)=>row.length && (rowIndex!==0||!this.hasHeadingsRow)).map(row=>{
      const entry = {};
      usedFields.forEach(field=>{
        if(this.hasLastName && field.multicolumn){
            entry[field.key] = field.multicolumn.fields.map((mfield, idx)=>row[this.selectedOptions[field.index+'.'+idx]]).filter(val=>!!val).join(' ');
        }else if(new RegExp('^'+this.customQuestionPrefix).test(field.key)){
          const questionId = field.key.replace(this.customQuestionPrefix, '');
          entry['customQuestions'] = entry['customQuestions'] || {};
          const response = row[this.selectedOptions[field.index]] || '';
          entry['customQuestions'][questionId] = response === 'true' || response === 'false' ? response === 'true' : response;
        }else{
          entry[field.key] = row[this.selectedOptions[field.index]] || '';
        }
      })
      return entry;
    })
    this.dialogRef.close({ type:this.data.type, entries })
  }

}

export interface ImportDialogData { // serves as both input and output data!
  type: ImportType,
  sheet?: string[][]
}
